import { apiFetch, apiSend } from './client'
import {
  AnalysisAreaResponse,
  AnalysisHours,
  BriefFeedbackSaved,
  CameraBaseline,
  ChatReplyResponse,
  type DisplayCropContext,
  DisplayCropResponse,
  type SchedulePhase,
  ScheduleVersionResponse,
  StashedViewCreated,
} from './schemas'

/** POST /api/sites/{siteId}/alerts/{alertId}/acknowledge */
export function acknowledgeAlert(siteId: string, alertId: number): Promise<void> {
  return apiSend(`/api/sites/${encodeURIComponent(siteId)}/alerts/${alertId}/acknowledge`, {
    method: 'POST',
  })
}

/** 9 Oct 2026 (Dori): POST /api/sites/{siteId}/needs-attention/dismiss --
 *  hide one Needs attention item (admin). The same occurrence stays
 *  hidden; a new one (e.g. the camera moves again) shows again. */
export function dismissNeedsAttention(siteId: string, itemId: string, dismissKey = ''): Promise<void> {
  return apiSend(`/api/sites/${encodeURIComponent(siteId)}/needs-attention/dismiss`, {
    method: 'POST',
    body: JSON.stringify({ item_id: itemId, dismiss_key: dismissKey }),
  })
}

/** Zonoo: AI Photo Comparison item 4 -- POST .../evidence/retry-comparison.
 *  A controlled, one-shot retry after a failed comparison; a no-op on the
 *  server unless that pair's comparison is currently 'failed'. Usable by
 *  a view-only client (the "evidence.compare" capability, scoped to their
 *  assigned sites -- same pattern as brief feedback). The caller
 *  re-fetches the evidence query afterward to pick up the new
 *  'pending'/polling state rather than trusting this response's body. */
export function retryEvidenceComparison(
  siteId: string,
  briefType: 'daily' | 'weekly',
  date: string | null = null,
): Promise<void> {
  const segment = briefType === 'daily' ? 'daily-brief' : 'weekly-summary'
  const qs = date ? `?date=${encodeURIComponent(date)}` : ''
  return apiSend(
    `/api/sites/${encodeURIComponent(siteId)}/${segment}/evidence/retry-comparison${qs}`,
    {
      method: 'POST',
    },
  )
}

/** POST /api/sites/{siteId}/phases/{phaseId}/close */
export function closePhase(siteId: string, phaseId: number): Promise<void> {
  return apiSend(`/api/sites/${encodeURIComponent(siteId)}/phases/${phaseId}/close`, {
    method: 'POST',
  })
}

/** BL-004: queue a manual baseline regeneration. Idempotent on the server. */
export function regenerateCameraBaseline(siteId: string, cameraId: string): Promise<void> {
  return apiSend(
    `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/baseline/regenerate`,
    { method: 'POST' },
  )
}

/** 2026-10-01: directly correct the current baseline's wording (e.g. the
 *  AI called a loft apartment a corridor) instead of only being able to
 *  re-run the AI and hope it reads the photo correctly next time. No LLM
 *  call -- creates a new baseline row with the human-written text,
 *  404s if there's no baseline yet to correct. */
export function editCameraBaseline(
  siteId: string,
  cameraId: string,
  description: string,
): Promise<CameraBaseline> {
  return apiFetch(
    `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/baseline`,
    CameraBaseline,
    { method: 'PATCH', body: JSON.stringify({ description }) },
  )
}

/** WP2A4: save (create or replace) the camera's Analysis Area. Coordinates
 *  are normalized [0,1]; the server re-validates range and re-derives a new
 *  compatible baseline for the camera. */
export function setCameraAnalysisArea(
  siteId: string,
  cameraId: string,
  area: { x: number; y: number; width: number; height: number },
): Promise<AnalysisAreaResponse> {
  return apiFetch(
    `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/analysis-area`,
    AnalysisAreaResponse,
    { method: 'POST', body: JSON.stringify(area) },
  )
}

/** WP2A4 section 2 item 5: remove the Analysis Area, returning the camera
 *  to full-frame analysis. */
export function removeCameraAnalysisArea(siteId: string, cameraId: string): Promise<void> {
  return apiSend(
    `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/analysis-area`,
    { method: 'DELETE' },
  )
}

/** Camera Page UI/UX item 6: save the camera's automatic-analysis
 *  window. camera.edit required server-side; prospective only -- never
 *  triggers reanalysis of anything already captured. */
export function setCameraAnalysisHours(
  siteId: string,
  cameraId: string,
  hours: { analysis_start_time: string; analysis_end_time: string; checks_per_hour?: number; zone?: string },
): Promise<AnalysisHours> {
  return apiFetch(
    `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/analysis-hours`,
    AnalysisHours,
    { method: 'PUT', body: JSON.stringify(hours) },
  )
}

/** Save (create or replace) the editor's pan/zoom for one view of a
 *  camera's photo ("grid" or "detail"). Coordinates are normalized
 *  [0,1]; purely a display setting, never touches AI analysis. */
export function setCameraDisplayCrop(
  siteId: string,
  cameraId: string,
  context: DisplayCropContext,
  rect: { x: number; y: number; width: number; height: number },
): Promise<DisplayCropResponse> {
  return apiFetch(
    `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/display-crop/${context}`,
    DisplayCropResponse,
    { method: 'POST', body: JSON.stringify(rect) },
  )
}

/** Clear the editor's saved pan/zoom for one view, returning it to its
 *  built-in default framing. */
export function removeCameraDisplayCrop(
  siteId: string,
  cameraId: string,
  context: DisplayCropContext,
): Promise<void> {
  return apiSend(
    `/api/sites/${encodeURIComponent(siteId)}/cameras/${encodeURIComponent(cameraId)}/display-crop/${context}`,
    { method: 'DELETE' },
  )
}

/** WP-SCHED: edit a still-draft schedule version before accepting it --
 *  e.g. correcting a date the extraction got wrong. Send only the
 *  fields that changed; omitted fields keep their current value. */
export function updateScheduleVersion(
  siteId: string,
  versionId: number,
  body: { project_brief?: string; phases?: SchedulePhase[] },
): Promise<ScheduleVersionResponse> {
  return apiFetch(
    `/api/sites/${encodeURIComponent(siteId)}/schedule-versions/${versionId}`,
    ScheduleVersionResponse,
    { method: 'PUT', body: JSON.stringify(body) },
  )
}

/** WP-polish (2026-09-23): start editing the currently accepted schedule
 *  -- copies it into a new draft (via update/accept as usual) rather than
 *  touching the accepted version in place. Fails with 400 if a draft is
 *  already awaiting review. */
export function reviseAcceptedScheduleVersion(siteId: string): Promise<ScheduleVersionResponse> {
  return apiFetch(
    `/api/sites/${encodeURIComponent(siteId)}/schedule-versions/revise-accepted`,
    ScheduleVersionResponse,
    { method: 'POST' },
  )
}

/** WP-SCHED: promote a draft to the site's accepted schedule. From this
 *  point on it is what the Site Update planning note, and every other
 *  schedule-aware feature, uses. */
export function acceptScheduleVersion(
  siteId: string,
  versionId: number,
): Promise<ScheduleVersionResponse> {
  return apiFetch(
    `/api/sites/${encodeURIComponent(siteId)}/schedule-versions/${versionId}/accept`,
    ScheduleVersionResponse,
    { method: 'POST' },
  )
}

/** WP-SCHED: reject a draft outright without accepting it. Never touches
 *  the currently accepted schedule (if any). */
export function discardScheduleVersion(siteId: string, versionId: number): Promise<void> {
  return apiSend(`/api/sites/${encodeURIComponent(siteId)}/schedule-versions/${versionId}`, {
    method: 'DELETE',
  })
}

/** CDV-029: stash a serialised view payload server-side and return its
 *  short id. Used by the dispatcher when the JSON would otherwise burst
 *  the URL budget; the URL becomes `?sala=ref:<id>`. */
export function postViewIntent(payload: string): Promise<StashedViewCreated> {
  return apiFetch('/api/chat/views', StashedViewCreated, {
    method: 'POST',
    body: JSON.stringify({ payload }),
  })
}

export type ChatPageContext =
  | { scope: 'portfolio' }
  | { scope: 'site'; site_id: string }
  | { scope: 'camera'; site_id: string; camera_id: string }
  | { scope: 'alerts'; site_id: string }

interface SendChatArgs {
  message: string
  conversationId: string
  pageContext: ChatPageContext
}

/**
 * POST /api/chat — non-streaming. Kept as a fallback for tests and
 * environments where the streaming endpoint isn't available.
 */
export function sendChat({ message, conversationId, pageContext }: SendChatArgs) {
  return apiFetch('/api/chat', ChatReplyResponse, {
    method: 'POST',
    body: JSON.stringify({
      message,
      conversation_id: conversationId,
      page_context: pageContext,
    }),
  })
}

interface StreamChatHandlers {
  onToken: (text: string) => void
  onDone: (info: { conversation_id: string; llm_call_id: number | null }) => void
  onError: (message: string) => void
  signal?: AbortSignal
}

/**
 * POST /api/chat/stream — consumes the text/event-stream response and
 * fans out token / done / error events to the supplied handlers.
 *
 * EventSource doesn't support POST, so we use fetch + a manual SSE
 * parser on top of the response body's ReadableStream.
 */
export async function streamChat(args: SendChatArgs, handlers: StreamChatHandlers): Promise<void> {
  const { message, conversationId, pageContext } = args
  const { onToken, onDone, onError, signal } = handlers

  let response: Response
  try {
    const csrfMatch =
      typeof document !== 'undefined' ? document.cookie.match(/(?:^|;\s*)cc_csrf=([^;]+)/) : null
    const csrf = csrfMatch?.[1] ? decodeURIComponent(csrfMatch[1]) : null
    response = await fetch('/api/chat/stream', {
      method: 'POST',
      headers: {
        Accept: 'text/event-stream',
        'Content-Type': 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId,
        page_context: pageContext,
      }),
      signal,
    })
  } catch (err) {
    onError(err instanceof Error ? err.message : 'Network error')
    return
  }

  if (!response.ok || !response.body) {
    onError(`HTTP ${response.status}`)
    return
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''

  try {
    while (true) {
      const { value, done } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // Drain complete event blocks (separated by blank line).
      let separator = buffer.indexOf('\n\n')
      while (separator !== -1) {
        const block = buffer.slice(0, separator)
        buffer = buffer.slice(separator + 2)
        const event = parseSseBlock(block)
        if (event) {
          if (event.name === 'token' && typeof event.data?.text === 'string') {
            onToken(event.data.text)
          } else if (event.name === 'done') {
            onDone({
              conversation_id: String(event.data?.conversation_id ?? conversationId),
              llm_call_id: (event.data?.llm_call_id as number | null) ?? null,
            })
          } else if (event.name === 'error') {
            onError(String(event.data?.message ?? 'Stream error'))
          }
        }
        separator = buffer.indexOf('\n\n')
      }
    }
  } catch (err) {
    if (signal?.aborted) return
    onError(err instanceof Error ? err.message : 'Stream read failed')
  }
}

interface ParsedSseEvent {
  name: string
  data: Record<string, unknown> | null
}

function parseSseBlock(block: string): ParsedSseEvent | null {
  let name = ''
  let dataLine = ''
  for (const line of block.split('\n')) {
    if (line.startsWith('event:')) name = line.slice(6).trim()
    else if (line.startsWith('data:')) dataLine = line.slice(5).trim()
  }
  if (!name) return null
  let data: Record<string, unknown> | null = null
  if (dataLine) {
    try {
      data = JSON.parse(dataLine) as Record<string, unknown>
    } catch {
      data = null
    }
  }
  return { name, data }
}

/** POST /api/sites/{siteId}/brief-feedback -- save or change the current
 *  user's rating of a Daily Brief or Weekly Summary card. One current
 *  rating per (site, user, brief_type, period); calling again with a
 *  different rating replaces it rather than adding a second one. */
export interface BriefFeedbackArgs {
  briefType: 'daily' | 'weekly'
  periodStart: string
  periodEnd: string
  rating: 'correct' | 'incorrect' | 'not_enough_evidence'
  briefText: string
}

export function saveBriefFeedback(
  siteId: string,
  args: BriefFeedbackArgs,
): Promise<BriefFeedbackSaved> {
  return apiFetch(`/api/sites/${encodeURIComponent(siteId)}/brief-feedback`, BriefFeedbackSaved, {
    method: 'POST',
    body: JSON.stringify({
      brief_type: args.briefType,
      period_start: args.periodStart,
      period_end: args.periodEnd,
      rating: args.rating,
      brief_text: args.briefText,
    }),
  })
}

/** 12 Oct 2026 (Dori): mark a phase started / finished (or undo) from
 *  Max's panel -- admin only (site.edit). The server uses today's date
 *  in the site's time zone. */
export function setPhaseProgress(
  siteId: string,
  phaseId: number,
  action: 'start' | 'finish' | 'undo',
  date?: string,
): Promise<void> {
  return apiSend(`/api/sites/${encodeURIComponent(siteId)}/phases/${phaseId}/progress`, {
    method: 'POST',
    body: JSON.stringify(date ? { action, date } : { action }),
  })
}

/** 14 Oct 2026: "Not yet" on one of Max's suggestions (admin only). */
export function dismissMaxSuggestion(siteId: string, phaseId: number): Promise<void> {
  return apiSend(`/api/sites/${encodeURIComponent(siteId)}/max-suggestions/${phaseId}/dismiss`, {
    method: 'POST',
    body: '{}',
  })
}
